import { useEffect, useState } from 'react';

/**
 * Which section of the showing document the reader is currently in, for the contents rail.
 *
 * **An `IntersectionObserver`, not a scroll handler.** The live page recomputes every anchor's
 * `getBoundingClientRect()` on every scroll event, which is a forced layout per frame per section
 * — sixteen of them, since both documents are in the DOM. An observer is told when a boundary is
 * crossed and costs nothing in between.
 *
 * The root margin is the sticky chrome: 72px of header plus 58px of tab bar plus the gap, negative
 * at the top so a heading counts as reached when it clears the bar rather than when it touches the
 * window. The bottom margin keeps only the topmost visible section active — without it every
 * heading on screen would be "current" at once.
 *
 * @param ids Anchor element ids, in document order.
 * @param enabled False for the document that is hidden; its anchors intersect nothing and would
 * otherwise leave the rail pointing at whichever section the *other* document last reported.
 */
export function useActiveSection(ids: readonly string[], enabled: boolean): string | null {
    const [active, setActive] = useState<string | null>(null);
    const key = ids.join('|');

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const order = key.split('|');
        const visible = new Set<string>();

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        visible.add(entry.target.id);
                    } else {
                        visible.delete(entry.target.id);
                    }
                }

                setActive(order.find((id) => visible.has(id)) ?? null);
            },
            { rootMargin: '-154px 0px -70% 0px' }
        );

        for (const id of order) {
            const element = document.getElementById(id);

            if (element !== null) {
                observer.observe(element);
            }
        }

        return () => observer.disconnect();
    }, [key, enabled]);

    // Nothing has crossed the line yet — at the top of the document, that is the first section.
    return active ?? (ids.length > 0 ? ids[0] : null);
}
